<template>
  <button
    type="button"
    class="DestinationCard"
    :aria-label="t('ui.components.destinationCard.ariaLabel', { destinationName })"
    @click="emit('click')"
  >
    <div class="DestinationCard-hero">
      <img
        class="DestinationCard-image"
        :src="resolvedImageUrl"
        :alt="destinationName"
        loading="lazy"
        @error="onImageError"
      >

      <Tag
        v-if="groupLabel"
        class="DestinationCard-groupTag"
        :type="tagType"
        variant="filled"
        size="xs"
      >
        {{ groupLabel }}
      </Tag>

      <ActionButton
        class="DestinationCard-chevron"
        tag="span"
        variant="secondary"
        size="sm"
        rightIcon="chevron-right"
        aria-hidden="true"
      />
    </div>

    <div class="DestinationCard-body">
      <div class="DestinationCard-cityInfo">
        <div class="DestinationCard-title">
          <span class="DestinationCard-destinationName">{{ destinationName }}</span>
          <span v-if="country" class="DestinationCard-country">{{ country }}</span>
        </div>

        <div class="DestinationCard-priceRow">
          <p class="DestinationCard-priceLabel DestinationCard-priceFrom">
            <I18nT keypath="ui.components.destinationCard.from">
              <template #price>
                <span class="DestinationCard-price">{{ price }}</span>
              </template>
            </I18nT>
          </p>

          <p class="DestinationCard-priceLabel">
            {{ t('ui.components.destinationCard.priceSuffixFirstLine') }}
          </p>

          <p class="DestinationCard-priceLabel">
            {{ t('ui.components.destinationCard.priceSuffixSecondLine') }}
          </p>
        </div>
      </div>

      <div v-if="flightTzDates.length" class="DestinationCard-times">
        <ChipTime
          v-for="(time, index) of flightTzDates"
          :key="index"
          :date="time"
        />
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { getTimezoneDate, normalizeTimezone } from '@cvo/domain';
import { computed, ref, watch } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import ChipTime from '../../atoms/ChipTime/ChipTime.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import type { DestinationCardProps, DestinationCardEmits } from './DestinationCard.model';

const props = withDefaults(defineProps<DestinationCardProps>(), {
  flightTimes: () => [],
  tagType: 'brand-secondary',
});

const emit = defineEmits<DestinationCardEmits>();

const { t } = useI18n();

const hasImageError = ref(false);

watch(() => props.imageUrl, () => {
  hasImageError.value = false;
});

const resolvedImageUrl = computed(() => {
  return hasImageError.value && props.fallbackImageUrl ? props.fallbackImageUrl : props.imageUrl;
});

function onImageError() {
  hasImageError.value = true;
}

/** Flight departure times converted to their station's local timezone. */
const flightTzDates = computed(() => props.flightTimes.map(({ utc, zoneId }) => getTimezoneDate(new Date(utc), normalizeTimezone(zoneId))));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .DestinationCard {
    display: flex;
    flex-direction: column;
    text-align: left;
    overflow: hidden;
    border-radius: vars.$atoms-actionbutton-primary-lg-border-radius;
    background-color: vars.$organisms-card-destination-bg-color;
    isolation: isolate;
    cursor: pointer;

    transition: box-shadow vars.$transition-base ease-in-out;
    box-shadow: vars.$shadow-md;

    &-hero {
      position: relative;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      flex: 1 1 0%;
      padding: vars.$dimensions-d-3;

      @include responsive.mobile {
        min-height: vars.$dimensions-d-24;
      }
    }

    &-image {
      position: absolute;
      inset: 0;
      inline-size: 100%;
      block-size: 100%;
      object-fit: cover;
    }

    &-groupTag {
      position: relative;
    }

    &-chevron {
      position: relative;
      flex-shrink: 0;
      margin-inline-start: auto;
    }

    &-body {
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      gap: vars.$dimensions-d-3;
      padding: vars.$dimensions-d-4;
    }

    &-cityInfo {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: vars.$dimensions-d-2;
    }

    &-title {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-0-5;
    }

    &-destinationName {
      @include text.text(md, vars.$typography-weight-strong);
      color: vars.$organisms-card-destination-text-color;
    }

    &-country {
      @include text.text(xs);
      color: vars.$organisms-card-destination-secondary-text-color;
    }

    &-priceRow {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      text-align: end;
    }

    &-priceLabel {
      @include text.text(xs);
      margin: 0;
      color: vars.$organisms-card-destination-secondary-text-color;
    }

    &-priceFrom {
      white-space: nowrap;
    }

    &-price {
      @include text.text(md, vars.$typography-weight-strong);
      color: vars.$organisms-card-destination-highlight-text-color;
    }

    &-times {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }

      @media (pointer: coarse) {
        flex-wrap: nowrap;
      }
    }

    &:hover {
      box-shadow: vars.$shadow-lg;
    }

    &:focus-visible {
      outline: vars.$dimensions-d-0-25 solid vars.$organisms-card-destination-highlight-text-color;
      outline-offset: vars.$dimensions-d-0-5;
    }
  }

  // Override the default ActionButton sizing so the decorative chevron renders as a compact circular icon button.
  .DestinationCard-chevron.Button {
    min-width: vars.$dimensions-d-8;
    border-radius: vars.$dimensions-d-30;
    border: none;
  }
}
</style>
